import React, { useRef, useState, useCallback, useEffect, useLayoutEffect, useMemo, useId } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface SelectOption {
  value: string
  label: string
  /** Small secondary line shown beneath the label in the dropdown list. */
  description?: string
  icon?: React.ReactNode
}

export interface SelectProps {
  options: SelectOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  error?: string
  id?: string
  disabled?: boolean
  /** Marks the select as required visually and for assistive technology. */
  required?: boolean
  className?: string
  /** Extra classes for the trigger button, e.g. a compact height in dense forms. */
  triggerClassName?: string
  clearable?: boolean
  /** Filter options by typing (use for long lists, e.g. countries). */
  searchable?: boolean
  searchPlaceholder?: string
}

interface ListPosition {
  top?: number
  bottom?: number
  left: number
  width: number
  maxHeight: number
  strategy: 'absolute' | 'fixed'
}

const inputBaseClasses =
  'box-border h-11 w-full rounded-xl border border-gray-200 bg-white/95 px-3.5 text-sm text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-150 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100'

export function Select({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  label,
  error,
  id: idProp,
  disabled = false,
  required = false,
  className,
  triggerClassName,
  clearable = true,
  searchable = false,
  searchPlaceholder = 'Search…',
}: SelectProps): React.ReactElement {
  const reactId = useId()
  const id = idProp ?? `select-${reactId}`
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const portalContentRef = useRef<HTMLDivElement>(null)
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [listPosition, setListPosition] = useState<ListPosition | null>(null)

  const filteredOptions = useMemo(() => {
    if (!searchable) return options
    const q = searchQuery.trim().toLowerCase()
    if (!q) return options
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.description?.toLowerCase().includes(q) ?? false),
    )
  }, [searchable, options, searchQuery])

  const listOptions = searchable ? filteredOptions : options

  const selectedOption = options.find((o) => o.value === value)
  const displayLabel = selectedOption ? selectedOption.label : placeholder
  const hasValue = value !== ''

  const close = useCallback(() => {
    setOpen(false)
    setHighlightedIndex(0)
    setSearchQuery('')
  }, [])

  const selectIndex = useCallback(
    (index: number) => {
      const opt = listOptions[index]
      if (opt) {
        onChange(opt.value)
        close()
      }
    },
    [listOptions, onChange, close],
  )

  const updateListPosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const dialogContent = trigger.closest<HTMLElement>('[data-dialog-content]')
    const viewportHeight = window.innerHeight
    const preferredHeight = searchable ? 320 : 272
    const gutter = 8

    if (dialogContent) {
      const containerRect = dialogContent.getBoundingClientRect()
      const spaceBelow = containerRect.bottom - rect.bottom
      const spaceAbove = rect.top - containerRect.top
      const openUpward = spaceBelow < 220 && spaceAbove > spaceBelow
      const maxHeight = Math.max(120, Math.min(preferredHeight, (openUpward ? spaceAbove : spaceBelow) - gutter))

      setPortalContainer(dialogContent)
      const containerContentHeight = dialogContent.scrollHeight
      setListPosition({
        ...(openUpward
          ? {
              bottom: containerContentHeight - (rect.top - containerRect.top + dialogContent.scrollTop) + 4,
            }
          : {
              top: rect.bottom - containerRect.top + dialogContent.scrollTop + 4,
            }),
        left: rect.left - containerRect.left + dialogContent.scrollLeft,
        width: rect.width,
        maxHeight,
        strategy: 'absolute',
      })
      return
    }

    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top
    const openUpward = spaceBelow < 220 && spaceAbove > spaceBelow
    const maxHeight = Math.max(120, Math.min(preferredHeight, (openUpward ? spaceAbove : spaceBelow) - gutter))

    setPortalContainer(document.body)
    setListPosition({
      ...(openUpward ? { bottom: viewportHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      left: rect.left,
      width: rect.width,
      maxHeight,
      strategy: 'fixed',
    })
  }, [searchable])

  useLayoutEffect(() => {
    if (!open) return
    updateListPosition()
    const onScrollOrResize = (): void => {
      updateListPosition()
    }
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [open, updateListPosition])

  useEffect(() => {
    if (!open) return
    const el = listRef.current
    if (!el) return
    const item = el.querySelector<HTMLElement>('[data-selected="true"]')
    if (item) item.scrollIntoView({ block: 'nearest' })
  }, [open, value])

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (portalContentRef.current?.contains(target)) return
      close()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      close()
      containerRef.current?.querySelector<HTMLElement>('button')?.focus()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      setOpen(true)
      const idx = options.findIndex((o) => o.value === value)
      setHighlightedIndex(idx >= 0 ? idx : 0)
      return
    }
    if (e.key === 'ArrowDown' && open) {
      e.preventDefault()
      setHighlightedIndex((index) => Math.min(index + 1, listOptions.length - 1))
    }
    if (e.key === 'ArrowUp' && open) {
      e.preventDefault()
      setHighlightedIndex((index) => Math.max(index - 1, 0))
    }
    if ((e.key === 'Enter' || e.key === ' ') && open) {
      e.preventDefault()
      selectIndex(highlightedIndex)
    }
  }

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.min(i + 1, listOptions.length - 1))
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.max(i - 1, 0))
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      selectIndex(highlightedIndex)
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
      containerRef.current?.querySelector<HTMLElement>('button')?.focus()
    }
  }

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {label && (
        <label
          htmlFor={id}
          className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400"
        >
          {label}
          {required && <span className="text-danger"> *</span>}
        </label>
      )}
      <div ref={triggerRef} className="relative">
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-required={required || undefined}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={label || undefined}
          onClick={() => {
            if (disabled) return
            setOpen((o) => !o)
            if (!open) {
              const opts = searchable && searchQuery.trim() ? filteredOptions : options
              const idx = opts.findIndex((o) => o.value === value)
              setHighlightedIndex(idx >= 0 ? idx : 0)
            }
          }}
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            inputBaseClasses,
            'flex min-h-[44px] w-full cursor-pointer items-center justify-between gap-3 text-left',
            error ? 'border-danger focus:border-danger/70 focus:ring-danger/10' : null,
            disabled && 'cursor-not-allowed opacity-50',
            triggerClassName,
          )}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 truncate">
            {selectedOption?.icon}
            <span className={cn(!hasValue && 'text-gray-400 dark:text-gray-500')}>{displayLabel}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {clearable && hasValue && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear selection"
                onClick={(e) => {
                  e.stopPropagation()
                  onChange('')
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange('')
                  }
                }}
                className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-4 w-4" />
              </span>
            )}
            <ChevronDown className={cn('h-4 w-4 text-gray-400 transition-transform', open && 'rotate-180')} />
          </span>
        </button>
        {open &&
          listPosition &&
          portalContainer &&
          createPortal(
            <div
              ref={portalContentRef}
              data-multiselect-dropdown
              className="pointer-events-auto overflow-hidden rounded-2xl border border-gray-200/80 bg-white/98 shadow-[0_20px_55px_rgba(15,23,42,0.18)] backdrop-blur dark:border-gray-700 dark:bg-gray-800/98"
              style={{
                position: listPosition.strategy,
                top: listPosition.top,
                bottom: listPosition.bottom,
                left: listPosition.left,
                width: listPosition.width,
                zIndex: 9999,
              }}
            >
              {searchable && (
                <div className="border-b border-gray-100 p-2.5 dark:border-gray-700">
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value)
                      setHighlightedIndex(0)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault()
                        setHighlightedIndex((index) =>
                          Math.min(index + 1, listOptions.length - 1),
                        )
                      }
                      if (e.key === 'ArrowUp') {
                        e.preventDefault()
                        setHighlightedIndex((index) => Math.max(index - 1, 0))
                      }
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        selectIndex(highlightedIndex)
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault()
                        close()
                        containerRef.current?.querySelector<HTMLElement>('button')?.focus()
                      }
                    }}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    className={cn(inputBaseClasses, 'h-10 py-1.5')}
                    autoComplete="off"
                    autoFocus
                  />
                </div>
              )}
              <ul
                ref={listRef}
                role="listbox"
                aria-activedescendant={
                  listOptions[highlightedIndex] ? `option-${listOptions[highlightedIndex].value}` : undefined
                }
                tabIndex={-1}
                onKeyDown={handleListKeyDown}
                className="overflow-auto p-1.5"
                style={{ maxHeight: listPosition.maxHeight }}
              >
                {listOptions.length === 0 ? (
                  <li className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">No matches</li>
                ) : (
                  listOptions.map((opt, i) => (
                    <li
                      key={opt.value}
                      id={`option-${opt.value}`}
                      role="option"
                      aria-selected={opt.value === value}
                      data-selected={opt.value === value}
                      onClick={() => selectIndex(i)}
                      onMouseEnter={() => setHighlightedIndex(i)}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-sm transition-colors',
                        opt.value === value && 'bg-primary/10 text-primary dark:bg-primary/20',
                        i === highlightedIndex && opt.value !== value && 'bg-gray-100 dark:bg-gray-700/80',
                        i === highlightedIndex && opt.value === value && 'bg-primary/15 dark:bg-primary/25',
                      )}
                    >
                      {opt.icon}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{opt.label}</span>
                        {opt.description && (
                          <span
                            className={cn(
                              'mt-0.5 block truncate text-xs',
                              opt.value === value
                                ? 'text-primary/70 dark:text-blue-200/80'
                                : 'text-gray-500 dark:text-gray-400',
                            )}
                          >
                            {opt.description}
                          </span>
                        )}
                      </span>
                      {opt.value === value && (
                        <span className="shrink-0 text-primary" aria-hidden>
                          ✓
                        </span>
                      )}
                    </li>
                  ))
                )}
              </ul>
            </div>,
            portalContainer,
          )}
      </div>
      {error && (
        <p id={id ? `${id}-error` : undefined} className="mt-1 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

Select.displayName = 'Select'
